import React, { useEffect, useState, useRef, useId, useLayoutEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type InsightBriefing = {
  metrics: {
    label: string;
    value: string;
  }[];
  signals: string[];
  actions: string[];
  actionResponses?: Record<string, string>;
};
const INSIGHT_BRIEFINGS: Record<number, InsightBriefing> = {
  8: {
    metrics: [{
      label: 'Confident error rate',
      value: '22%'
    }, {
      label: 'Support focus',
      value: 'Flexbox, Grid, positioning'
    }, {
      label: 'Priority',
      value: 'Above other core modules'
    }],
    signals: ['CSS Fundamentals has a 22% confident error rate, above the other core modules.', 'Learners are most often confident but incorrect on Flexbox, Grid, and positioning.', 'Encouraging more practice time on these topics before the next attempt can improve readiness.'],
    actions: ['Show learners struggling with CSS Fundamentals', 'Draft a study nudge for these learners', 'Recommend how these learners should prepare']
  },
  10: {
    metrics: [{
      label: 'Average first-attempt score',
      value: '64.93%'
    }, {
      label: 'Average final score',
      value: '78.4%'
    }, {
      label: 'Widest skill gap',
      value: '18 points'
    }],
    signals: ['Learners gain about 13 points between their first and final attempts.', 'The widest gap between skills is 18 points, concentrated in CSS Fundamentals.', 'Comparing a single learner against the group average shows where coaching will pay off fastest.'],
    actions: ['Compare the weakest and strongest skills', 'Show learners with the largest first-to-final gap', 'Summarize the trend over the last 30 days']
  },
  11: {
    metrics: [{
      label: 'Learners below pass threshold',
      value: '3'
    }, {
      label: 'Pass threshold',
      value: '70%'
    }, {
      label: 'Group average',
      value: '78.4%'
    }],
    signals: ['3 learners are still below the 70% pass threshold.', 'The same learners appear in the lowest quartile across two or more skills.', 'A short, focused intervention with this group would lift the overall average most.'],
    actions: ['List the learners below the pass threshold', 'Suggest a support plan for this group', 'Compare this group against the top performers']
  },
  12: {
    metrics: [{
      label: 'Registration completed',
      value: '55 learners'
    }, {
      label: 'Active this month',
      value: '80%'
    }, {
      label: 'Completed all modules',
      value: '10%'
    }],
    signals: ['Participation is strong at 80%, but only 10% of learners have finished every module.', 'Completion fell 20% from the prior 30-day period while activity rose 20%.', 'The gap sits with active learners who have one or two modules remaining.'],
    actions: ['Identify learners with one or two modules remaining', 'Send a completion reminder to active learners', 'Review where learners stall most often']
  },
  13: {
    metrics: [{
      label: 'Skill module seats left',
      value: '70 of 240'
    }, {
      label: 'Pathway seats left',
      value: '30 of 40'
    }, {
      label: 'Pathways in use',
      value: '2'
    }],
    signals: ['70 skill module seats and 30 pathway seats are still available.', 'Only 2 pathways are in use, so learners are being assigned mostly individual skills.', 'Grouping related skills into a pathway makes progress easier to track.'],
    actions: ['Suggest a pathway for unassigned learners', 'Show which skills have unused capacity', 'Plan the next assignment round']
  },
  15: {
    metrics: [{
      label: 'Average gain on retake',
      value: '+21 points'
    }, {
      label: 'Learners needing 2+ attempts',
      value: '4'
    }, {
      label: 'Module with most attempts',
      value: 'Leading with Effective Communication'
    }],
    signals: ['Every learner who retook a module improved, by an average of 21 points.', 'Leading with Effective Communication takes up to three attempts to pass.', 'First-attempt scores, not effort, are the limiting factor across modules.'],
    actions: ['Show learners who needed multiple attempts', 'Recommend how learners should prepare before a first attempt', 'Compare first-attempt and final scores by module']
  },
  14: {
    metrics: [{
      label: 'Urgent items',
      value: '2'
    }, {
      label: 'Oldest open item',
      value: '6 days'
    }, {
      label: 'Completed this week',
      value: '4'
    }],
    signals: ['Urgent items are the fastest route to unblocking learners.', 'The oldest open item has been waiting 6 days.', 'Most remaining items are reminders that can be handled in one batch.'],
    actions: ['Show the urgent items first', 'Draft outreach for the oldest open item', 'Group the remaining reminders into one batch']
  }
};
export interface AIInsightPage {
  /** Short name for the view this insight belongs to, e.g. a chart type */
  label: string;
  headline: string;
  insightBeforeMetric: string;
  highlightedMetric: string;
  insightAfterMetric?: string;
  explanation: string;
  insightId?: number;
  briefing?: InsightBriefing;
}
export interface AIInsightPopoverProps {
  headline: string;
  insightBeforeMetric: string;
  highlightedMetric: string;
  insightAfterMetric?: string;
  explanation: string;
  insightId?: number;
  briefing?: InsightBriefing;
  align?: 'left' | 'right';
  /**
   * Optional set of insights, one per view of the same section. Stepping
   * through them can also change the view via onPageChange.
   */
  pages?: AIInsightPage[];
  /** Index of the page to show. Omit to let the popover manage it. */
  activePageIndex?: number;
  onPageChange?: (index: number) => void;
}
export function AIInsightPopover({
  headline,
  insightBeforeMetric,
  highlightedMetric,
  insightAfterMetric = '',
  explanation,
  insightId,
  briefing: customBriefing,
  align = 'right',
  pages,
  activePageIndex,
  onPageChange
}: AIInsightPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [internalPageIndex, setInternalPageIndex] = useState(0);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const suppressFocusOpenRef = useRef(false);
  const skipClickRef = useRef(false);
  const popoverId = useId();
  const dismiss = (returnFocus = false) => {
    setIsOpen(false);
    setIsPinned(false);
    if (returnFocus) {
      suppressFocusOpenRef.current = true;
      triggerRef.current?.focus();
      window.setTimeout(() => {
        suppressFocusOpenRef.current = false;
      }, 0);
    }
  };
  useLayoutEffect(() => {
    if (!isOpen) return;
    const positionPopover = () => {
      const rect = popoverRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 16;
      const leftOverflow = viewportPadding - rect.left;
      const rightOverflow = rect.right - (window.innerWidth - viewportPadding);
      setHorizontalOffset(leftOverflow > 0 ? leftOverflow : rightOverflow > 0 ? -rightOverflow : 0);
    };
    positionPopover();
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      // Clicking anywhere outside always closes, pinned or not
      if (!rootRef.current?.contains(event.target as Node)) {
        dismiss();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss(true);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isPinned]);
  const closeWhenFocusLeaves = () => {
    window.requestAnimationFrame(() => {
      if (!isPinned && !rootRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    });
  };
  const handleTriggerPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    suppressFocusOpenRef.current = true;
    window.setTimeout(() => {
      suppressFocusOpenRef.current = false;
    }, 0);
    if (event.pointerType !== 'mouse') {
      skipClickRef.current = true;
      setIsOpen(open => !open);
      setIsPinned(pinned => !pinned);
    }
  };
  const togglePinnedPopover = () => {
    setIsPinned(pinned => !pinned);
    setIsOpen(true);
  };
  const insightPages: AIInsightPage[] = pages && pages.length > 0 ? pages : [{
    label: '',
    headline,
    insightBeforeMetric,
    highlightedMetric,
    insightAfterMetric,
    explanation,
    insightId,
    briefing: customBriefing
  }];
  const rawPageIndex = activePageIndex !== undefined ? activePageIndex : internalPageIndex;
  const pageIndex = Math.min(Math.max(rawPageIndex, 0), insightPages.length - 1);
  const activePage = insightPages[pageIndex];
  const goToPage = (index: number) => {
    const nextIndex = (index + insightPages.length) % insightPages.length;
    setInternalPageIndex(nextIndex);
    onPageChange?.(nextIndex);
    setIsPinned(true);
    setIsOpen(true);
  };
  return <div ref={rootRef} className="relative inline-flex" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => {
    if (!isPinned) setIsOpen(false);
  }} onBlurCapture={closeWhenFocusLeaves} data-id="element-231" data-mp="4fsalz1k5y32">
      <button ref={triggerRef} type="button" aria-label={`${isPinned ? 'Unpin' : 'Pin'} AI insight: ${activePage.headline}`} aria-expanded={isOpen} aria-pressed={isPinned} aria-controls={popoverId} onPointerDown={handleTriggerPointerDown} onFocus={() => {
      if (!suppressFocusOpenRef.current) setIsOpen(true);
    }} onClick={() => {
      if (skipClickRef.current) {
        skipClickRef.current = false;
        return;
      }
      togglePinnedPopover();
    }} className={`inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${isPinned ? 'border-[#30718D] bg-[#30718D]/10' : 'border-[#30718D]/30'}`} data-id="element-232" data-mp="3rnm8fmq9q8i9">
        <Sparkles size={15} aria-hidden="true" data-id="element-233" data-mp="1eh63r55wapfo" />
      </button>

      <AnimatePresence data-id="element-234" data-mp="1jf7n7r78gqro">
        {isOpen && <motion.section ref={popoverRef} id={popoverId} role="dialog" aria-label="AI insight" initial={{
        opacity: 0,
        x: horizontalOffset,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        x: horizontalOffset,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: horizontalOffset,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: 'easeOut'
      }} className={`absolute top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-4 shadow-xl ${align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-235" data-mp="1jcbpa2vdy238">
            <div className="mb-3 flex items-center gap-2 text-[#30718D]" data-id="element-236" data-mp="1ac5hac8c20d1">
              <Sparkles size={16} aria-hidden="true" data-id="element-237" data-mp="1p0097qeio0p2" />
              <p className="text-xs font-semibold uppercase tracking-wide" data-id="element-238" data-mp="1k28kt6d2ywpm">
                AI insights
              </p>
            </div>
            {insightPages.length > 1 && activePage.label && <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400" data-id="element-239" data-mp="rdt3ueae24bn">
                {activePage.label}
              </p>}
            <h3 className="text-sm font-bold text-gray-900" data-id="element-240" data-mp="15b7ybxfi12ah">
              {activePage.headline}
            </h3>
            <p className="mt-1.5 text-sm font-medium leading-snug text-gray-800" data-id="element-241" data-mp="11e3hg00c5drc">
              {activePage.insightBeforeMetric}{' '}
              <span className="text-[#007558]" data-id="element-242" data-mp="2crnsbm5je7gs">
                {activePage.highlightedMetric}
              </span>
              {activePage.insightAfterMetric}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-600" data-id="element-243" data-mp="2p9i0dadnchs6">
              {activePage.explanation}
            </p>
            {insightPages.length > 1 && <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3" data-id="element-244" data-mp="2aebwou20wv20">
                <button type="button" onClick={() => goToPage(pageIndex - 1)} aria-label="Previous insight" className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:border-[#30718D] hover:text-[#30718D] focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-245" data-mp="2fdetjr7unuyw">
                  <ChevronLeft size={15} aria-hidden="true" data-id="element-246" data-mp="3lz8xzih8il27" />
                </button>
                <p aria-live="polite" className="text-xs font-medium text-gray-500" data-id="element-247" data-mp="3qqhaindv2mjl">
                  Insight {pageIndex + 1} of {insightPages.length}
                </p>
                <button type="button" onClick={() => goToPage(pageIndex + 1)} aria-label="Next insight" className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:border-[#30718D] hover:text-[#30718D] focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-248" data-mp="2302w2yyeb0hi">
                  <ChevronRight size={15} aria-hidden="true" data-id="element-249" data-mp="2gtkjiuu7cxct" />
                </button>
              </div>}
            <button type="button" onClick={() => {
          const briefing = activePage.briefing || (activePage.insightId ? INSIGHT_BRIEFINGS[activePage.insightId] : undefined);
          const metrics = briefing?.metrics || [{
            label: 'Key metric',
            value: activePage.highlightedMetric
          }];
          const signals = briefing?.signals || [activePage.explanation];
          const actions = briefing?.actions || ['Help me plan the next step'];
          dismiss();
          openPhoebeChat({
            prompt: `Tell me more about this insight: ${activePage.headline}`,
            response: buildInsightResponse(activePage.headline, metrics, signals, actions),
            suggestedQuestions: actions,
            suggestionResponses: Object.fromEntries(actions.map(action => [action, briefing?.actionResponses?.[action] || buildActionResponse(action, activePage.headline, signals)]))
          });
        }} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-250" data-mp="2c2c8xvh4z5y2">
              Learn more with Phoebe <ArrowRight size={16} aria-hidden="true" data-id="element-251" data-mp="3k2anf23y65fx" />
            </button>
          </motion.section>}
      </AnimatePresence>
    </div>;
}
function buildInsightResponse(headline: string, metrics: {
  label: string;
  value: string;
}[], signals: string[], actions: string[]) {
  return `**${headline}**\n\n**Snapshot**\n${metrics.map(metric => `• ${metric.label}: ${metric.value}`).join('\n')}\n\n**What the data shows**\n${signals.map(signal => `• ${signal}`).join('\n')}\n\n**Suggested actions**\n${actions.map(action => `• ${action}`).join('\n')}\n\nChoose an action below and I’ll help you turn it into a practical next step.`;
}
function buildActionResponse(action: string, headline: string, signals: string[]) {
  return `Here’s a focused way to move forward with “${action}.”\n\n**Why this matters**\n${headline}\n\n**Recommended approach**\n1. Start with this signal: ${signals[0]}\n2. Complete one focused follow-up this week.\n3. Review the relevant outcome in 7 days and adjust if needed.\n\nWould you like help drafting the outreach, identifying the right learners, or defining a success measure?`;
}